import commonUtil, { CommonUtils } from "../../util/common-utils";
import { OgpApi } from "../../util/ogp/ogp-api";
import { AdTypes } from "../../constants/ad-types";
import { postsAdsFormBase } from "./posts-ads-form";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export class PostsAdsRender {
    public static ogpApi = new OgpApi();

    public static getAdLinkUrl(ad: UnknownRecord): string {
        const raw = String(ad.link_url || ad.url_link || "").trim();
        return raw;
    }

    public static wrapAdContent(inner: string, linkUrl: string): string {
        const safeLink = CommonUtils.escapeHtmlAttribute(String(linkUrl || "").trim());
        if (!safeLink) {
            return inner;
        }

        return `
            <a class="ad-slot__link" href="${safeLink}" target="_blank" rel="noopener noreferrer">
                ${inner}
            </a>
        `.trim();
    }

    public static wrapProductLinkerCardContent(inner: string, linkUrl: string): string {
        const safeLink = CommonUtils.escapeHtmlAttribute(String(linkUrl || "").trim());
        if (!safeLink) {
            return `
                <div class="ad-slot__link post-product-linker__card-link ad">
                    ${inner}
                </div>
            `.trim();
        }

        return `
            <a class="ad-slot__link post-product-linker__card-link ad" href="${safeLink}" target="_blank" rel="noopener noreferrer">
                ${inner}
            </a>
        `.trim();
    }

    public static buildAdHtml(ad: UnknownRecord, compact = false): string {
        const rawPath = String(ad.image_path || "").trim();
        const imagePath = rawPath ? CommonUtils.resolveUrl(rawPath) : "";
        const adTitle = commonUtil.escapeHtml(String(ad.ad_title || "Sponsored"));
        const adBody = commonUtil.escapeHtml(String(ad.ad_body || ""));
        const variantClass = compact ? "ad-slot--compact" : "ad-slot--linker";
        const imageVariantClass = compact ? "ad-slot__image--compact" : "ad-slot__image--linker";
        const linkUrl = this.getAdLinkUrl(ad);
        const adId = CommonUtils.escapeHtmlAttribute(String(ad.ad_id || ""));
        const adType = CommonUtils.escapeHtmlAttribute(String(ad.ad_type || ""));
        const content = `
            <div class="ad-slot__image-wrap">
                <img class="ad-slot__image ${imageVariantClass}" src="${imagePath}" alt="${adTitle}" loading="lazy" />
                <span class="ad-slot__badge" aria-label="Advertisement">AD</span>
            </div>
            <div class="ad-slot__content">
                <div class="ad-slot__title">${adTitle}</div>
                <div class="ad-slot__body">${adBody}</div>
            </div>
        `.trim();

        if (compact) {
            return `
                <div class="ad-slot ${variantClass}" data-ad-slot="1" data-ad-id="${adId}" data-ad-type="${adType}">
                    ${this.wrapAdContent(content, linkUrl)}
                </div>
            `.trim();
        }

        return `
            <div class="ad-slot ${variantClass}" data-ad-slot="1" data-ad-id="${adId}" data-ad-type="${adType}">
                ${this.wrapProductLinkerCardContent(content, linkUrl)}
            </div>
        `.trim();
    }

    public static async fetchOgpDataFromAdLink(ad: UnknownRecord): Promise<UnknownRecord | null> {
        const linkUrl = this.getAdLinkUrl(ad);
        if (!linkUrl) return null;

        try {
            const response = await this.ogpApi.fetchOgp(linkUrl);
            if (!response.ok) return null;

            const payload = (await response.json()) as { data?: unknown };
            const ogpData = payload && isRecord(payload.data) ? payload.data : null;
            return ogpData;
        } catch {
            return null;
        }
    }

    public static buildOgpAdHtml(ad: UnknownRecord, ogpData: UnknownRecord | null): string {
        const fallbackImagePath = String(ad.image_path || "").trim();
        const ogpImage = String(ogpData?.image || "").trim();
        const imagePath = ogpImage || (fallbackImagePath ? CommonUtils.resolveUrl(fallbackImagePath) : "");

        const linkUrl = String(ogpData?.url || this.getAdLinkUrl(ad) || "").trim();
        const ogpTitleRaw = String(ogpData?.ogp_title || ad.ad_title || "Sponsored").trim();
        const generalDescriptionRaw = String(ogpData?.general_description || ad.ad_body || "").trim();

        const adTitle = commonUtil.escapeHtml(ogpTitleRaw || "Sponsored");
        const adBody = commonUtil.escapeHtml(generalDescriptionRaw);
        const safeImagePath = CommonUtils.escapeHtmlAttribute(imagePath);

        const content = `
            <div class="ad-slot__image-wrap">
                <img class="ad-slot__image ad-slot__image--linker" src="${safeImagePath}" alt="${adTitle}" loading="lazy" />
                <span class="ad-slot__badge" aria-label="Advertisement">AD</span>
            </div>
            <div class="ad-slot__content">
                <div class="ad-slot__title">${adTitle}</div>
                <div class="ad-slot__body">${adBody}</div>
            </div>
        `.trim();

        return `
            <div class="ad-slot ad-slot--linker" data-ad-slot="1" data-ad-ogp="1" data-ad-id="${CommonUtils.escapeHtmlAttribute(String(ad.ad_id || ""))}" data-ad-type="${CommonUtils.escapeHtmlAttribute(String(ad.ad_type || ""))}">
                ${this.wrapProductLinkerCardContent(content, linkUrl)}
            </div>
        `.trim();
    }

    // Mirrors PostService::shouldShowPlaceholderAd() — this client-side fill path
    // (used for infinite-scroll-appended cards and for cards a modal save
    // resets to "pending", see modal-state-handler.ts) has its own ad pool and
    // must re-apply the same display_rate_percent gate, not just assume the ad
    // it was handed should always be shown.
    public static shouldShowPlaceholderAd(ad: UnknownRecord): boolean {
        const raw = ad.display_rate_percent;
        if (raw === null || raw === undefined || raw === "") return true;

        const rate = Math.max(0, Math.min(100, Number(raw) || 0));
        return Math.floor(Math.random() * 100) + 1 <= rate;
    }

    public static applyPlaceholderAd(card: HTMLElement, ad: UnknownRecord): void {
        const placeholder = postsAdsFormBase.getPlaceholder(card);
        if (!(placeholder instanceof HTMLElement)) return;
        if (placeholder.dataset.adApplied === "1") return;

        placeholder.dataset.adApplied = "1";
        placeholder.outerHTML = this.buildAdHtml(ad, true);
    }

    // The display_rate_percent roll said "don't show" — resolve the slot to the
    // same static empty box SSR renders for that case (board-placeholder.blade.php's
    // $ssrAdsHandled branch) instead of leaving the "loading" skeleton stuck
    // forever, and mark it applied so later app:content:inserted events don't
    // keep re-rolling it.
    public static skipPlaceholderAd(card: HTMLElement): void {
        const placeholder = postsAdsFormBase.getPlaceholder(card);
        if (!(placeholder instanceof HTMLElement)) return;
        if (placeholder.dataset.adApplied === "1") return;

        placeholder.dataset.adApplied = "1";
        placeholder.dataset.placeholderVariant = "empty";
        placeholder.innerHTML = "";
    }

    public static applyProductLinkerAd(card: HTMLElement, ad: UnknownRecord): void {
        const linker = postsAdsFormBase.getLinker(card);
        if (!(linker instanceof HTMLElement)) return;
        if (linker.dataset.adApplied === "1") return;

        linker.dataset.adApplied = "1";
        linker.innerHTML = this.buildAdHtml(ad, false);
    }

    public static async applyProductLinkerOgpAd(card: HTMLElement, ad: UnknownRecord): Promise<void> {
        if (String(ad.ad_type || "").toUpperCase() !== AdTypes.PRODUCT_LINKER_OGP) {
            this.applyProductLinkerAd(card, ad);
            return;
        }

        const linker = postsAdsFormBase.getLinker(card);
        if (!(linker instanceof HTMLElement)) return;
        if (linker.dataset.adApplied === "1") return;

        const ogpData = await this.fetchOgpDataFromAdLink(ad);
        linker.dataset.adApplied = "1";
        linker.innerHTML = ogpData ? this.buildOgpAdHtml(ad, ogpData) : this.buildAdHtml(ad, false);
    }
}
